import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useAnalytics from '@/hooks/useAnalytics';

import { DashboardLayoutAccountSidebar } from '../dashboard-layout/DashboardLayout';
import { LeftPaneMenu } from '../navigation';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import BallotIcon from '@/ui-library/BallotIcon';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  CodeBracketSquareIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { primitiveColors, semanticColors, BORDER_RADIUS } from '@/theme/tokens';
import { useAuth } from '../auth/AuthProvider';
import { getRememberedChatPath } from '@/utils/lastChatPath';
import LanguageSelector from '@/components/language-selector/LanguageSelector';
import { DEFAULT_LANGUAGE } from '@/constants/languages';
import PageLoader from '@/ui-library/PageLoader';
import { TOGGLE_SX } from '@/ui-library/switch.styles';
import useSettingsSections from '@/hooks/useSettingsSections';
import SaveStatus from './SaveStatus';
import NavModeToggle from './NavModeToggle';
import { setNavMode, type NavMode } from '@/hooks/useNavMode';
import SectionSaveRow from './SectionSaveRow';
import AutoCompleteAddressInput from '../signup/AutoCompleteAddressInput';
import { useNotification } from '../ui-library/NotificationProvider';
import { ExternalLink } from '../ui-library/ExternalLink';
import { EXTERNAL_URLS } from '@/constants/urls';
import useApi from '@/hooks/useApi';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import BallotStatusDot from '@/components/ballot/BallotStatusDot';
import { getBallotDotState, hasUpcomingElection } from '@/types/ballotRecords';
import type { BallotRecordSummary } from '@/types/ballotRecords';
import { extraElectionCount, formatBallotRecordName } from '@/utils/ballotRecordName';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAuthProvider = (auth0Id?: string): string => {
  if (!auth0Id) return 'Auth0';
  if (auth0Id.startsWith('google-oauth2|')) return 'Google';
  if (auth0Id.startsWith('windowslive|')) return 'Microsoft';
  if (auth0Id.startsWith('facebook|')) return 'Facebook';
  if (auth0Id.startsWith('apple|')) return 'Apple';
  return 'Auth0';
};

const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return 'U';
};

// ─── Style constants ──────────────────────────────────────────────────────────

const SECTION_CARD_SX = {
  border: `1px solid ${semanticColors.borderLight}`,
  borderRadius: BORDER_RADIUS,
  p: 2.5,
  mb: 2,
  backgroundColor: primitiveColors.white,
} as const;

// Every settings input shares this so the sizes can't drift apart again.
// 14px matches the surrounding body text. The exception is touch pointers:
// iOS Safari zooms the viewport when a focused field is under 16px and the
// page's viewport meta sets no maximum-scale (raising it would break
// pinch-zoom). Keying that off `pointer: coarse` rather than a width
// breakpoint keeps a narrow desktop window — which never zooms — at 14px.
const INPUT_SX = {
  '& .MuiInputBase-root': {
    borderRadius: BORDER_RADIUS,
    fontSize: '14px',
    '@media (pointer: coarse)': { fontSize: '16px' },
  },
} as const;

const FIELD_LABEL_SX = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  color: semanticColors.textTertiary,
  textTransform: 'uppercase' as const,
  mb: 0.5,
} as const;

// Ballot rows shown before the list collapses behind "Show All" (matches the
// left menu + status popup).
const VISIBLE_BALLOT_ROWS = 5;

// ─── Main Component ───────────────────────────────────────────────────────────

export const SettingsContainer = () => {
  const [leftPaneOpen, setLeftPaneOpen] = useState(false);
  const [triggerLeftPaneAccountMenu, setTriggerLeftPaneAccountMenu] = useState(false);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { t, i18n } = useTranslation('settings');
  const { t: tBallot } = useTranslation('ballot');
  const { t: tCommon } = useTranslation('common');
  const [ballotToDelete, setBallotToDelete] = useState<BallotRecordSummary | null>(null);
  const [showAllBallots, setShowAllBallots] = useState(false);

  // Delete account state
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { user, isInitializing, signOut, signIn } = useAuth();
  const { showNotification } = useNotification();
  const query = useApi();
  const {
    records: ballotRecordSummaries,
    activeBallotId,
    activateBallot,
    deleteRecord,
  } = useBallotRecords();
  // Only labeled entries (past + the address's soonest-upcoming) get a row here,
  // matching the left menu; later elections stay tab-only in the ballot view.
  const visibleBallotRecords = ballotRecordSummaries.filter((r) => r.is_visible_entry !== false);

  // The save model lives in `useSettingsSections`: switches and the language
  // select write on change, while name/address/phone commit through the inline
  // Save row inside their own card. There is no page-level unsaved state.
  const {
    phone,
    address,
    addressError,
    phoneError,
    emailConsent,
    phoneConsent,
    firstName,
    lastName,
    setFirstName,
    setLastName,
    language,
    autoSaveBallots,
    settingsLoaded,
    groups,
    saveName,
    cancelName,
    saveAddress,
    cancelAddress,
    savePhone,
    cancelPhone,
    toggleEmailConsent,
    togglePhoneConsent,
    toggleAutoSaveBallots,
    selectLanguage,
    navMode,
    selectNavMode,
    handleAddressChange,
    handleAddressSelect,
    handlePhoneChange,
    handlePhoneKeyDown,
    clearAddressField,
    loadUserSettings,
  } = useSettingsSections();

  // Labels shared by every inline status chip and save row on the page.
  const statusLabels = { savingLabel: t('saving'), savedLabel: t('saved') };

  // Enter in either name field commits, the same as pressing Save.
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && groups.name.canSave && groups.name.state !== 'saving') {
      e.preventDefault();
      void saveName();
    }
  };

  // Deleting removes the whole address group; a "ballots → none" transition
  // (effect below) blanks the address entry so it returns empty, matching the
  // backend clearing address_components and the ballot view's landing state.
  const confirmDeleteBallot = useCallback(async () => {
    if (!ballotToDelete) return;
    const id = ballotToDelete.id;
    setBallotToDelete(null);
    await deleteRecord(id);
    // The trash button that opened the dialog just unmounted with its row;
    // move focus to the section heading so it isn't dropped to the body.
    requestAnimationFrame(() => {
      document.getElementById('voting-info-section-heading')?.focus();
    });
  }, [ballotToDelete, deleteRecord]);

  // Blank the address entry when the last ballot is deleted (records go 0), so
  // the field the no-ballots state reveals starts empty rather than showing the
  // just-removed address.
  const hadBallotsRef = useRef(false);
  useEffect(() => {
    if (visibleBallotRecords.length === 0 && hadBallotsRef.current) {
      clearAddressField();
    }
    hadBallotsRef.current = visibleBallotRecords.length > 0;
  }, [visibleBallotRecords.length, clearAddressField]);

  const handleMenuClick = () => {
    setLeftPaneOpen((prev) => !prev);
  };

  const handleMenuClose = () => {
    setLeftPaneOpen(false);
  };

  // Always exit to a chat route (not navigate(-1), which nested Settings ↔ Help).
  // Signed-in: return to the remembered ``/chat/:id``; guest: keep ``?guest=true``
  // so their on-device chat rehydrates.
  const handleGoBack = useCallback(() => {
    if (!user) {
      navigate('/chat?guest=true');
      return;
    }
    const stored = getRememberedChatPath();
    navigate(stored && stored.startsWith('/chat') ? stored : '/chat');
  }, [navigate, user]);

  const handleOpenBallot = useCallback(() => {
    analytics.track('Sample Ballot Viewed', {
      source: 'Left Menu - Settings Page',
    });
    navigate('/chat?state=ballot-open');
  }, [analytics, navigate]);

  useEffect(() => {
    if (!isInitializing && user && !settingsLoaded) {
      loadUserSettings();
    }
  }, [isInitializing, user, loadUserSettings, settingsLoaded]);

  const handleDeleteUserAccount = useCallback(async () => {
    setIsDeleting(true);
    try {
      showNotification(t('deletingAccount'), 'info');
      await query('/user', {
        method: 'DELETE',
        failMessage: t('failedToDelete'),
      });
      showNotification(t('accountDeleted'), 'success');
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      showNotification(t('failedToDelete'), 'error');
    } finally {
      setIsDeleting(false);
    }
  }, [showNotification, t, query, signOut, navigate]);

  // ─── Auth loading ──────────────────────────────────────────────────────────

  if (isInitializing) {
    return <PageLoader />;
  }

  // ─── Derived values ────────────────────────────────────────────────────────

  const provider = user ? getAuthProvider(user.auth0Id) : null;
  const initials = user ? getInitials(user.name, user.email) : null;

  // ─── Shared section cards ──────────────────────────────────────────────────

  const languageSection = (
    <Box sx={SECTION_CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
        <Box sx={{ mt: '2px', flexShrink: 0, lineHeight: 0 }}>
          <GlobeAltIcon
            style={{ width: 20, height: 20, color: semanticColors.textPrimary }}
            aria-hidden="true"
          />
        </Box>
        <Box>
          <Typography
            id="settings-language-label"
            sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }}
          >
            {t('language')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
            {t('languageDesc')}
          </Typography>
        </Box>
      </Box>
      <LanguageSelector
        value={language}
        onChange={selectLanguage}
        labelId="settings-language-label"
      />
      <Box sx={{ mt: 1 }}>
        <SaveStatus state={groups.language.state} error={groups.language.error} {...statusLabels} />
      </Box>
      {/* Catalogs are machine-translated (R1); this invites corrections.
          English is the source, so it's exempt. */}
      {i18n.language !== DEFAULT_LANGUAGE && (
        <Typography
          sx={{ mt: 1, fontSize: '12px', lineHeight: 1.4, color: semanticColors.textTertiary }}
        >
          {tCommon('translationNotice')}
        </Typography>
      )}
    </Box>
  );

  // Guests have no account to persist to, so their choice is local-only and
  // instant; the segmented control and the analytics event are otherwise
  // identical to the signed-in card, which persists through ``selectNavMode``.
  const handleGuestNavMode = (mode: NavMode) => {
    const previous = navMode;
    if (mode === previous) return;
    setNavMode(mode);
    analytics.track('Nav Mode Selected', { mode, previousMode: previous, authed: false });
  };

  // ``showStatus``: the save row belongs to the signed-in card, which has an
  // account write to report. A guest's change is local and immediate, so a
  // "Saved" line would describe a request that never happens.
  const renderNavModeSection = (onSelect: (mode: NavMode) => void, showStatus: boolean) => (
    <Box sx={SECTION_CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
        <Box sx={{ mt: '2px', flexShrink: 0, lineHeight: 0 }}>
          <DevicePhoneMobileIcon
            style={{ width: 20, height: 20, color: semanticColors.textPrimary }}
            aria-hidden="true"
          />
        </Box>
        <Box>
          <Typography
            id="settings-nav-mode-label"
            sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }}
          >
            {t('navMode')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
            {t('navModeDesc')}
          </Typography>
        </Box>
      </Box>
      <NavModeToggle
        value={navMode}
        onChange={onSelect}
        tabLabel={t('navModeTab')}
        sheetLabel={t('navModeSheet')}
        labelId="settings-nav-mode-label"
      />
      {showStatus && (
        <Box sx={{ mt: 1 }}>
          <SaveStatus state={groups.navMode.state} error={groups.navMode.error} {...statusLabels} />
        </Box>
      )}
    </Box>
  );

  const aiDataSection = (
    <Box sx={SECTION_CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ mt: '2px', flexShrink: 0, lineHeight: 0 }}>
          <CodeBracketSquareIcon
            style={{ width: 20, height: 20, color: semanticColors.textPrimary }}
            aria-hidden="true"
          />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }}>
            {t('aiDataTitle')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary, mt: 0.5 }}>
            {t('aiDataDescription')}{' '}
            <ExternalLink href={EXTERNAL_URLS.PRIVACY_POLICY} style={{ fontSize: '13px' }}>
              {t('seePrivacyPolicy')}
            </ExternalLink>
            .
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const legalSection = (
    <Box sx={SECTION_CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PrivacyTipIcon sx={{ color: semanticColors.textPrimary, fontSize: 20 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>{t('legalPrivacy')}</Typography>
      </Box>

      {[
        { label: t('termsAndConditions'), href: EXTERNAL_URLS.TERMS_OF_USE },
        { label: t('privacyPolicy'), href: EXTERNAL_URLS.PRIVACY_POLICY },
        { label: t('responsibleAI'), href: EXTERNAL_URLS.RESPONSIBLE_AI },
      ].map((item, i, arr) => (
        <Box key={item.label}>
          <Box
            component="a"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { opacity: 0.7 },
            }}
          >
            <Typography sx={{ fontSize: '14px' }}>{item.label}</Typography>
            <ArrowForwardIosIcon sx={{ fontSize: 13, color: semanticColors.textTertiary }} />
          </Box>
          {i < arr.length - 1 && <Divider sx={{ opacity: 0.5 }} />}
        </Box>
      ))}

      <Typography
        sx={{
          fontSize: '11px',
          color: semanticColors.textTertiary,
          fontStyle: 'italic',
          mt: 2,
          lineHeight: 1.5,
        }}
      >
        <Trans
          i18nKey="legalConfirmation"
          ns="settings"
          components={{
            termsLink: (
              <ExternalLink href={EXTERNAL_URLS.TERMS_OF_USE} style={{ fontSize: '11px' }} />
            ),
            privacyLink: (
              <ExternalLink href={EXTERNAL_URLS.PRIVACY_POLICY} style={{ fontSize: '11px' }} />
            ),
          }}
        />
      </Typography>
    </Box>
  );

  // ─── Guest view ────────────────────────────────────────────────────────────

  if (!user) {
    return (
      <>
        <DashboardLayoutAccountSidebar
          showWhiteStrip={true}
          onMenuClick={handleMenuClick}
          guestMode={true}
          hideAccountButton={true}
          menuOpen={leftPaneOpen}
          pageScroll={true}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: '85vw', md: '70vw', lg: '580px' },
              maxWidth: '100%',
              mx: 'auto',
              mb: 4,
              px: { xs: 2, sm: 3 },
              pt: 2,
              pb: 4,
            }}
          >
            <Button
              onClick={handleGoBack}
              variant="text"
              disableRipple
              startIcon={<ChevronLeftIcon sx={{ fontSize: '24px !important' }} />}
              sx={{
                background: 'none',
                color: 'inherit',
                padding: 0,
                minWidth: 0,
                boxShadow: 'none',
                textTransform: 'none',
                mb: 3,
                fontSize: '1.5rem',
                fontWeight: 700,
                lineHeight: 1.334,
                '&:hover': { background: 'none', opacity: 0.7 },
              }}
            >
              {t('title')}
            </Button>

            {/* Guest profile card */}
            <Box sx={SECTION_CARD_SX}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: semanticColors.surfaceNeutral,
                      color: semanticColors.textTertiary,
                      fontSize: '14px',
                    }}
                  >
                    ?
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.3 }}>
                      {t('browsingAsGuest')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                      {t('signInForSettings')}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  onClick={signIn}
                  variant="contained"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    borderRadius: BORDER_RADIUS,
                    px: 2,
                    flexShrink: 0,
                    backgroundColor: semanticColors.textPrimary,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
                    '&:active': { boxShadow: 'none' },
                  }}
                >
                  {t('signInSignUp')}
                </Button>
              </Box>
            </Box>

            {renderNavModeSection(handleGuestNavMode, false)}

            {aiDataSection}

            {legalSection}
          </Box>
        </DashboardLayoutAccountSidebar>
        <LeftPaneMenu
          isOpen={leftPaneOpen}
          onClose={handleMenuClose}
          guestMode={true}
          onOpenBallot={handleOpenBallot}
        />
      </>
    );
  }

  // ─── Authenticated view ────────────────────────────────────────────────────

  return (
    <>
      <DashboardLayoutAccountSidebar
        showWhiteStrip={true}
        onMenuClick={handleMenuClick}
        onAccountIconClick={() => {
          setLeftPaneOpen(true);
          setTriggerLeftPaneAccountMenu(true);
        }}
        menuOpen={leftPaneOpen}
        pageScroll={true}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: '85vw', md: '70vw', lg: '580px' },
            maxWidth: '100%',
            mx: 'auto',
            mb: 4,
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button
              onClick={handleGoBack}
              variant="text"
              disableRipple
              startIcon={<ChevronLeftIcon sx={{ fontSize: '24px !important' }} />}
              sx={{
                background: 'none',
                color: 'inherit',
                padding: 0,
                minWidth: 0,
                boxShadow: 'none',
                textTransform: 'none',
                fontSize: '1.5rem',
                fontWeight: 700,
                lineHeight: 1.334,
                '&:hover': { background: 'none', opacity: 0.7 },
              }}
            >
              {t('title')}
            </Button>
          </Box>

          {/* ─── Profile row ────────────────────────────────────────────────── */}
          <Box sx={SECTION_CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {user.picture ? (
                <Avatar
                  src={user.picture}
                  alt={user.name || user.email || t('common:userAvatarAlt')}
                  sx={{ width: 40, height: 40 }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    background: 'linear-gradient(135deg, #5B6CFF, #E86167)',
                    fontSize: '15px',
                    fontWeight: 700,
                  }}
                >
                  {initials}
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                  {t('signedInVia', { provider })}
                </Typography>
              </Box>
              <Button
                onClick={signOut}
                variant="outlined"
                size="small"
                startIcon={<ExitToAppIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  borderRadius: BORDER_RADIUS,
                  px: 1.5,
                  flexShrink: 0,
                  borderColor: semanticColors.borderLight,
                  color: semanticColors.textSecondary,
                  '&:hover': {
                    borderColor: semanticColors.borderMedium,
                    background: semanticColors.surfaceSubtle,
                  },
                }}
              >
                {t('signOut')}
              </Button>
            </Box>
          </Box>

          {/* ─── Loading spinner while settings fetch ───────────────────────── */}
          {!settingsLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {/* ─── Your Name ─────────────────────────────────────────────── */}
              <Box sx={SECTION_CARD_SX}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }}>
                      {t('yourName')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                      {t('yourNameDesc')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: '200px' }}>
                    <TextField
                      id="settings-first-name"
                      label={t('firstName')}
                      fullWidth
                      variant="outlined"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      onKeyDown={handleNameKeyDown}
                      placeholder="Jane"
                      inputProps={{ maxLength: 100 }}
                      size="small"
                      sx={INPUT_SX}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: '200px' }}>
                    <TextField
                      id="settings-last-name"
                      label={t('lastName')}
                      fullWidth
                      variant="outlined"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      onKeyDown={handleNameKeyDown}
                      placeholder="Doe"
                      inputProps={{ maxLength: 100 }}
                      size="small"
                      sx={INPUT_SX}
                    />
                  </Box>
                </Box>
                <SectionSaveRow
                  dirty={groups.name.dirty}
                  state={groups.name.state}
                  error={groups.name.error}
                  canSave={groups.name.canSave}
                  saveLabel={t('saveName')}
                  cancelLabel={t('cancel')}
                  {...statusLabels}
                  onSave={() => void saveName()}
                  onCancel={cancelName}
                />
              </Box>

              {/* ─── Language ────────────────────────────────────────────────── */}
              {languageSection}

              {renderNavModeSection(selectNavMode, true)}

              {/* ─── Ballots & Addresses (ballot list + auto-save) ──────────── */}
              <Box sx={SECTION_CARD_SX}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                  <Box sx={{ mt: '2px', flexShrink: 0, lineHeight: 0 }}>
                    <BallotIcon size={20} color={semanticColors.textPrimary} />
                  </Box>
                  <Box>
                    <Typography
                      id="voting-info-section-heading"
                      tabIndex={-1}
                      sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3, outline: 'none' }}
                    >
                      {t('ballotsAndAddresses')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                      {t('ballotsAndAddressesDesc')}
                    </Typography>
                  </Box>
                </Box>

                {/* Ballots list — each (address, election) selection with its
                    address. Selecting one activates it (address + chat context
                    follow); deleting the last one reveals the address entry. */}
                {visibleBallotRecords.length > 0 ? (
                  <>
                    <Box
                      role="list"
                      id="settings-ballot-list"
                      aria-label={t('ballotsAndAddresses')}
                    >
                      {(showAllBallots
                        ? visibleBallotRecords
                        : visibleBallotRecords.slice(0, VISIBLE_BALLOT_ROWS)
                      ).map((record) => {
                        const name = formatBallotRecordName(
                          record,
                          i18n.language,
                          tBallot('records.unnamed')
                        );
                        const extra = extraElectionCount(record);
                        const isActive =
                          record.is_active_entry === true ||
                          activeBallotId === record.id ||
                          (!activeBallotId && record.is_current_address);
                        return (
                          <Box
                            role="listitem"
                            key={record.id}
                            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                          >
                            <ButtonBase
                              onClick={() => void activateBallot(record.id)}
                              aria-label={tBallot('records.openAriaLabel', { name })}
                              aria-pressed={isActive}
                              sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: '8px',
                                px: 1,
                                py: '8px',
                                pr: '40px',
                                textAlign: 'left',
                                borderRadius: BORDER_RADIUS,
                                backgroundColor: isActive
                                  ? semanticColors.overlaySelected
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: isActive
                                    ? semanticColors.overlaySelected
                                    : semanticColors.overlaySubtle,
                                },
                                '&:focus-visible': {
                                  outline: `2px solid ${primitiveColors.logoPurple}`,
                                  outlineOffset: '2px',
                                  zIndex: 1,
                                },
                              }}
                            >
                              <BallotStatusDot
                                state={getBallotDotState(
                                  record.status,
                                  hasUpcomingElection(record.elections),
                                  record.is_complete
                                )}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  noWrap
                                  sx={{
                                    fontSize: '14px',
                                    color: semanticColors.textBody,
                                    fontWeight: isActive ? 600 : 400,
                                  }}
                                >
                                  {name}
                                  {extra > 0 &&
                                    ` ${tBallot('records.moreElections', { count: extra })}`}
                                </Typography>
                                {(record.address_full || record.location_label) && (
                                  <Typography
                                    noWrap
                                    sx={{
                                      fontSize: '12px',
                                      // Darker on the selected-row overlay to hold AA contrast.
                                      color: isActive
                                        ? semanticColors.textBody
                                        : semanticColors.textTertiary,
                                    }}
                                  >
                                    {record.address_full || record.location_label}
                                  </Typography>
                                )}
                              </Box>
                            </ButtonBase>
                            <IconButton
                              size="small"
                              onClick={() => setBallotToDelete(record)}
                              aria-label={tBallot('records.deleteAriaLabel', { name })}
                              sx={{
                                position: 'absolute',
                                right: '8px',
                                color: semanticColors.textTertiary,
                                width: 28,
                                height: 28,
                                '&:hover': { color: semanticColors.danger },
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: '18px' }} />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>
                    {visibleBallotRecords.length > VISIBLE_BALLOT_ROWS && (
                      <ButtonBase
                        disableRipple
                        onClick={() => {
                          setShowAllBallots((prev) => !prev);
                          if (!showAllBallots) analytics.track('Ballot History Expanded');
                        }}
                        aria-expanded={showAllBallots}
                        aria-controls="settings-ballot-list"
                        aria-label={
                          showAllBallots
                            ? tBallot('records.showLessAria')
                            : tBallot('records.showAllAria')
                        }
                        sx={{
                          width: '100%',
                          display: 'block',
                          textAlign: 'center',
                          px: 1,
                          py: '6px',
                          borderRadius: BORDER_RADIUS,
                          '&:hover': { backgroundColor: semanticColors.overlaySubtle },
                          '&:focus-visible': {
                            outline: `2px solid ${primitiveColors.logoPurple}`,
                            outlineOffset: '-2px',
                            zIndex: 1,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: 12, lg: 13 },
                            fontWeight: 600,
                            color: semanticColors.brandPrimary,
                          }}
                        >
                          {showAllBallots
                            ? tBallot('records.showLess')
                            : tBallot('records.showAll')}
                        </Typography>
                      </ButtonBase>
                    )}
                  </>
                ) : (
                  <Box sx={{ py: 0.5 }}>
                    <Typography sx={FIELD_LABEL_SX}>{t('registeredVotingAddress')}</Typography>
                    <AutoCompleteAddressInput
                      value={address}
                      onChange={handleAddressChange}
                      onAddressSelect={handleAddressSelect}
                      placeholder={t('addressPlaceholder')}
                      error={!!addressError}
                      helperText={addressError}
                      size="small"
                      sx={{
                        ...INPUT_SX,
                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          top: '100%',
                          mt: '3px',
                        },
                      }}
                    />
                    <SectionSaveRow
                      dirty={groups.address.dirty}
                      state={groups.address.state}
                      error={groups.address.error}
                      canSave={groups.address.canSave}
                      saveLabel={t('saveAddress')}
                      cancelLabel={t('cancel')}
                      blockedHint={t('selectSuggestionToSave')}
                      {...statusLabels}
                      onSave={() => void saveAddress()}
                      onCancel={cancelAddress}
                    />
                  </Box>
                )}

                {/* Auto-save ballots */}
                <Divider sx={{ my: 1.5 }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    pt: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, mr: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        {t('autoSaveBallots')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                        {t('autoSaveBallotsDesc')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <Switch
                      checked={autoSaveBallots}
                      onChange={(e) => toggleAutoSaveBallots(e.target.checked)}
                      // Inert mid-write so a rapid on/off/on can't stack
                      // overlapping saves for the same flag.
                      disabled={groups.autoSaveBallots.state === 'saving'}
                      sx={TOGGLE_SX}
                      inputProps={{
                        role: 'switch',
                        'aria-checked': autoSaveBallots,
                        'aria-label': t('autoSaveBallots'),
                      }}
                    />
                  </Box>
                </Box>
                <SaveStatus
                  state={groups.autoSaveBallots.state}
                  error={groups.autoSaveBallots.error}
                  {...statusLabels}
                />
              </Box>

              {/* Ballot delete confirmation */}
              <Dialog
                open={Boolean(ballotToDelete)}
                onClose={() => setBallotToDelete(null)}
                aria-labelledby="settings-delete-ballot-title"
                aria-describedby="settings-delete-ballot-body"
              >
                <DialogTitle
                  id="settings-delete-ballot-title"
                  sx={{ fontSize: '16px', fontWeight: 700 }}
                >
                  {tBallot('records.deleteTitle')}
                </DialogTitle>
                <DialogContent>
                  <Typography id="settings-delete-ballot-body" sx={{ fontSize: '14px', mb: 2 }}>
                    {tBallot('records.deleteBody')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setBallotToDelete(null)} sx={{ textTransform: 'none' }}>
                      {t('cancel')}
                    </Button>
                    <Button
                      onClick={() => void confirmDeleteBallot()}
                      color="error"
                      variant="contained"
                      sx={{ textTransform: 'none' }}
                    >
                      {tBallot('records.delete')}
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>

              {/* ─── Notifications ───────────────────────────────────────────── */}
              <Box sx={SECTION_CARD_SX}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                  <CampaignIcon sx={{ color: semanticColors.textPrimary, mt: 0.2, fontSize: 20 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3 }}>
                      {t('notifications')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                      {t('notificationsDesc')}
                    </Typography>
                  </Box>
                </Box>

                {/* Email notifications toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1, mr: 2 }}>
                    <EmailIcon
                      sx={{
                        fontSize: 18,
                        color: semanticColors.textTertiary,
                        mt: 0.2,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        {t('emailNotifications')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                        {t('emailNotificationsDesc')}
                        <br />
                        {t('unsubscribeAnytime')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <Switch
                      checked={emailConsent}
                      onChange={(e) => toggleEmailConsent(e.target.checked)}
                      // Inert mid-write so a rapid on/off/on can't stack
                      // overlapping saves for the same flag.
                      disabled={groups.emailConsent.state === 'saving'}
                      sx={TOGGLE_SX}
                      inputProps={{
                        role: 'switch',
                        'aria-checked': emailConsent,
                        'aria-label': t('emailNotifications'),
                      }}
                    />
                  </Box>
                </Box>
                <SaveStatus
                  state={groups.emailConsent.state}
                  error={groups.emailConsent.error}
                  {...statusLabels}
                />

                <Divider sx={{ opacity: 0.4 }} />

                {/* SMS notifications toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1, mr: 2 }}>
                    <ChatBubbleOutlineIcon
                      sx={{
                        fontSize: 18,
                        color: semanticColors.textTertiary,
                        mt: 0.2,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        {t('textNotifications')}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                        {t('smsNotificationsDesc')}
                        <br />
                        {t('replyStopToUnsubscribe')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <Switch
                      checked={phoneConsent}
                      onChange={(e) => togglePhoneConsent(e.target.checked)}
                      // Inert mid-write so a rapid on/off/on can't stack
                      // overlapping saves for the same flag.
                      disabled={groups.phoneConsent.state === 'saving'}
                      sx={TOGGLE_SX}
                      inputProps={{
                        role: 'switch',
                        'aria-checked': phoneConsent,
                        'aria-label': t('textNotifications'),
                      }}
                    />
                  </Box>
                </Box>
                <SaveStatus
                  state={groups.phoneConsent.state}
                  error={groups.phoneConsent.error}
                  {...statusLabels}
                />

                {/* Phone number field — only shown when SMS notifications are enabled */}
                {phoneConsent && (
                  <Box
                    sx={{
                      pt: 1.5,
                      mt: 0.5,
                      borderTop: `1px solid ${semanticColors.borderLight}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: semanticColors.textTertiary }} />
                      <Typography sx={{ ...FIELD_LABEL_SX, mb: 0 }}>{t('phoneNumber')}</Typography>
                    </Box>
                    <TextField
                      value={phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder={t('phoneNumberPlaceholder')}
                      size="small"
                      type="tel"
                      error={!!phoneError}
                      helperText={phoneError}
                      slotProps={{
                        htmlInput: {
                          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (
                              e.key === 'Enter' &&
                              groups.phone.canSave &&
                              groups.phone.state !== 'saving'
                            ) {
                              e.preventDefault();
                              void savePhone();
                              return;
                            }
                            handlePhoneKeyDown(e);
                          },
                        },
                      }}
                      sx={{ width: '100%', ...INPUT_SX }}
                    />
                    <SectionSaveRow
                      dirty={groups.phone.dirty}
                      state={groups.phone.state}
                      error={groups.phone.error}
                      canSave={groups.phone.canSave}
                      saveLabel={t('savePhone')}
                      cancelLabel={t('cancel')}
                      blockedHint={t('addPhoneToEnable')}
                      {...statusLabels}
                      onSave={() => void savePhone()}
                      onCancel={cancelPhone}
                    />
                  </Box>
                )}
              </Box>

              {/* ─── AI & Data ─────────────────────────────────────────────────── */}
              {aiDataSection}

              {/* ─── Legal & Privacy ──────────────────────────────────────────── */}
              {legalSection}

              {/* ─── Account / Delete ─────────────────────────────────────────── */}
              <Box sx={SECTION_CARD_SX}>
                {!showDeleteConfirm ? (
                  <Box
                    onClick={() => setShowDeleteConfirm(true)}
                    role="button"
                    tabIndex={0}
                    aria-label={t('deleteAccount')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowDeleteConfirm(true);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                      cursor: 'pointer',
                      borderRadius: BORDER_RADIUS,
                      mx: -1,
                      px: 1,
                      py: 0.75,
                      '&:hover': { background: '#fff5f5' },
                      '&:focus-visible': {
                        outline: `2px solid ${semanticColors.danger}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <DeleteOutlineIcon
                      sx={{ color: semanticColors.danger, fontSize: 20, mt: 0.2, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography
                        sx={{ color: semanticColors.danger, fontWeight: 600, fontSize: '14px' }}
                      >
                        {t('deleteAccount')}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: semanticColors.textTertiary }}>
                        {t('deleteAccountDesc')}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                      <DeleteOutlineIcon
                        sx={{ color: semanticColors.danger, fontSize: 20, mt: 0.2, flexShrink: 0 }}
                      />
                      <Box>
                        <Typography
                          sx={{ color: semanticColors.danger, fontWeight: 600, fontSize: '14px' }}
                        >
                          {t('deleteAccount')}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: semanticColors.textBody,
                            mt: 0.5,
                            lineHeight: 1.5,
                          }}
                        >
                          {t('deleteAccountConfirm')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                        variant="text"
                        sx={{
                          textTransform: 'none',
                          fontSize: '13px',
                          borderRadius: BORDER_RADIUS,
                          color: semanticColors.textSecondary,
                          '&:hover': {
                            background: semanticColors.overlaySubtle,
                          },
                        }}
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        size="small"
                        onClick={handleDeleteUserAccount}
                        disabled={isDeleting}
                        variant="contained"
                        startIcon={
                          isDeleting ? (
                            <CircularProgress
                              size={12}
                              sx={{ color: semanticColors.textOnPrimary }}
                            />
                          ) : undefined
                        }
                        sx={{
                          textTransform: 'none',
                          fontSize: '13px',
                          borderRadius: BORDER_RADIUS,
                          backgroundColor: semanticColors.danger,
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: semanticColors.dangerHover,
                            boxShadow: 'none',
                          },
                          '&:active': { boxShadow: 'none' },
                          '&.Mui-disabled': { backgroundColor: semanticColors.disabledBackground },
                        }}
                      >
                        {t('yesDeleteAccount')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </DashboardLayoutAccountSidebar>

      <LeftPaneMenu
        isOpen={leftPaneOpen}
        onClose={handleMenuClose}
        guestMode={false}
        onOpenBallot={handleOpenBallot}
        shouldOpenAccountMenu={triggerLeftPaneAccountMenu}
        onAccountMenuOpened={() => setTriggerLeftPaneAccountMenu(false)}
      />
    </>
  );
};
